import { format, addYears } from 'date-fns';
import fp from 'lodash/fp';
import React from 'react';
import {
  Button,
  Col,
  Grid,
  Row,
  Tooltip,
  OverlayTrigger
} from 'react-bootstrap';
import { FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';
import { connect } from 'react-redux';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withHandlers,
  withProps,
  withStateHandlers
} from 'recompose';
import queryString from 'query-string';
import { bindActionCreators } from 'redux';
import { replace } from 'react-router-redux';
import exportCpdReportPdf from '../../../../src/reports/export-cpd-report-pdf';
import {
  dateFormats,
  exportCpdActivityMap,
  getCpdActivityTransform,
  mapCpdPoint,
  riskThresholds,
  cpdCategoryType
} from '../../../constants';
import cpdPointStatus from '../../../constants/cpd-point-status';
import { attachFullName, decodeIfString, fullName } from '../../../services';
import { currentUserIndustry } from '../../../state/modules/user';
import { cpdCategorysActions } from '../../../state/modules/areas-of-advice';
import { publisherActions } from '../../../state/modules/publisher';
import {
  downloadBlobStreamFile,
  getBlobStream,
  getCsvStream,
  unfixedFloor,
  unfixedCeil,
  writeDataToCsvStream
} from '../../../utils';
import { Icon, SortableTable, SelectFormField } from '../../components';
import { ProgressChart } from '../../partials';
import Loading from '../loading';
import {
  getCpdActivityColumns,
  mapCpdActivity as mapCpdActivityForCsv
} from '../../../services/cpd-activity';
import * as dateFnsCompat from 'utils/date-fns-compat';
import {
  cpdPointActions,
  exportCpdSelector
} from '../../../state/modules/cpd-points';

const formatDate = (date, dateFormat = dateFormats.LongDate) =>
  date !== '' && date !== undefined ? format(new Date(date), dateFormat) : '';

const mapRiskRateStyle = (riskRate) => {
  if (!riskRate) {
    return 'riskRating';
  } else if (riskRate === 'Low' || riskRate === '-') {
    return 'low-risk';
  } else if (riskRate === 'Medium') {
    return 'medium-risk';
  } else {
    return 'high-risk';
  }
};

const progressColumns = [
  {
    name: 'cpdCategoryForDisplay',
    display: () => 'CPD category',
    className: () => 'cpdCategory'
  },
  {
    name: 'trainingHoursMet',
    display: () => 'Training hrs met',
    className: () => 'trainingHoursMet'
  },
  {
    name: 'trainingPlanHrs',
    display: () => 'Training plan hrs',
    className: () => 'trainingPlanHrs'
  },
  {
    name: 'totalProgressHrs',
    display: () => (
      <span>
        Completed hrs{' '}
        <OverlayTrigger
          placement='bottom'
          overlay={
            <Tooltip id='tooltip-total-progress-hrs'>
              <p>
                Total completed hours reflect the sum of core topics hours, as
                sub topics are already mapped to these areas.
              </p>
            </Tooltip>
          }
        >
          <span
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              verticalAlign: 'middle'
            }}
          >
            <FiInfo className='info-icon' size={14} />
          </span>
        </OverlayTrigger>
      </span>
    ),
    className: () => 'totalProgressHrs'
  },
  {
    name: 'shortfallHrs',
    display: () => 'Shortfall hrs',
    className: () => 'shortfallHrs'
  },
  {
    name: 'totalProgress',
    display: () => 'Total progress %',
    className: () => 'totalProgress'
  },
  {
    name: 'shortfall',
    display: () => 'Shortfall %',
    className: () => 'shortfall%'
  },
  {
    name: 'daysLeft',
    display: () => 'Days left',
    className: () => 'daysLeft'
  },
  {
    name: 'riskRating',
    display: () => 'Risk rating',
    className: mapRiskRateStyle
  }
];

const activityColumns = (goals) => [
  {
    name: 'type',
    display: () => 'Type',
    className: () => 'type'
  },
  {
    name: 'cpdCategory',
    display: () => 'CPD category',
    className: () => 'cpdCategory'
  },
  {
    name: 'provider',
    display: () => 'Provider',
    className: () => 'provider'
  },
  {
    name: 'activityContent',
    display: () => 'Content title',
    className: () => 'contentTile'
  },
  {
    name: 'cpdPoints',
    display: () => 'CPD hours',
    className: () => 'cpdHours'
  },
  {
    name: 'status',
    display: () => 'Passed?',
    className: () => 'status'
  },
  {
    name: 'dateCompleted',
    display: () => 'Date',
    className: () => 'dateCompleted'
  },
  {
    name: 'externalCpdId',
    display: () => 'Activity Source',
    className: () => 'activitySource'
  },
  ...fp.map(({ goal }) => ({
    name: goal.name,
    display: () => goal.name,
    className: () => 'goal'
  }))(goals)
];

const filterUserCpdCategory = ({ cpdCategorys }, allCpdCategorys) =>
  fp.flow(
    fp.map(({ primaryStat, derivedStat, cpdCategoryId, children }) => ({
      cpdCategoryId,
      title: decodeIfString(
        fp.get('name')(
          fp.find(({ id }) => id === cpdCategoryId)(allCpdCategorys)
        )
      ),
      categoryType: decodeIfString(
        fp.get('categoryType')(
          fp.find(({ id }) => id === cpdCategoryId)(allCpdCategorys)
        )
      ),
      children,
      ...primaryStat,
      ...derivedStat
    })),
    fp.sortBy([({ shortFall }) => -1 * shortFall])
  )(cpdCategorys);

const mapAllCategories = (cpdCategorys, allCpdCategorys) =>
  fp.flow(
    fp.map((aoa) => {
      if (fp.isEmpty(aoa.children)) {
        const aoaWithClassname = { className: 'normalCpdCategory', ...aoa };
        return {
          aoa: aoaWithClassname
        };
      } else {
        const aoaWithClassname = { className: 'parentCpdCategory', ...aoa };
        const children = fp.flow(
          fp.map((childCpdCategory) => ({
            className: 'childCpdCategory',
            ...childCpdCategory
          }))
        )(
          filterUserCpdCategory({ cpdCategorys: aoa.children }, allCpdCategorys)
        );
        return {
          aoa: aoaWithClassname,
          children
        };
      }
    }),
    fp.sortBy([
      ({ children }) => {
        if (children) {
          return children.length;
        } else {
          return 0;
        }
      },
      ({ aoa }) => aoa.title
    ])
  )(filterUserCpdCategory(cpdCategorys, allCpdCategorys));

const mapCpdActivity = (cpdCategorys, publishers, userGoals) =>
  fp.flow(
    fp.filter((cpdPoint) => cpdPoint.status !== cpdPointStatus.draft),
    fp.map((cpdPoint) => {
      const mapCpdCategoryName = (cpdCategoryId) =>
        fp.flow(
          attachFullName,
          fp.find(
            (industryCpdCategory) => industryCpdCategory.id === cpdCategoryId
          ),
          fp.get('name')
        )(cpdCategorys);
      const hasMultipleCpdCategory = cpdPoint.cpdPointAllocation.length > 1;
      const cpdCategoryName = hasMultipleCpdCategory
        ? 'Multiple'
        : fp
            .flow(
              fp.map('cpdCategoryId'),
              fp.map(mapCpdCategoryName)
            )(cpdPoint.cpdPointAllocation)
            .join(', ');

      const userGoalMatch = userGoals.reduce((pre, { goal }) => {
        return {
          ...pre,
          [goal.name]: fp.includes(goal.id)(cpdPoint.goals) ? 'Yes' : 'No'
        };
      }, {});
      return {
        ...cpdPoint,
        userGoalMatch,
        hasMultipleCpdCategory,
        cpdCategoryName
      };
    }),
    fp.map((cpdPoint) => {
      const mapped = fp.flow(
        fp.keys,
        fp.map((key) => [
          key,
          fp.flow(
            mapCpdPoint(cpdCategorys, publishers),
            getCpdActivityTransform(key)(exportCpdActivityMap),
            decodeIfString
          )(cpdPoint)
        ]),
        (pairs) =>
          pairs.concat([
            ['hasMultipleCpdCategory', cpdPoint.hasMultipleCpdCategory]
          ]),
        fp.fromPairs
      )(exportCpdActivityMap);
      return {
        ...mapped,
        ...cpdPoint.userGoalMatch
      };
    })
  );

const mapRiskRate = (riskRate) =>
  riskRate < riskThresholds.medium
    ? 'Low'
    : riskRate < riskThresholds.high
    ? 'Medium'
    : 'High';

const ProgressReport = ({
  afsl,
  filter: { fromDate, toDate },
  user,
  cpdProgress,
  progressTableRows,
  activityTableRows,
  reportBreadCrumb,
  exportPdf,
  exportCsv,
  goals,
  defaultProgressLabel,
  trainingPlanSnapshot,
  showCpdPeriodSelect,
  yearsFromNow,
  goTo
}) => (
  <div className='report-layout'>
    <section id='header--report' key='report-header' className='layout--header'>
      <Grid>
        <Row className='sm-gutter'>
          <Col xs={12} md={5}>
            {reportBreadCrumb}
          </Col>
        </Row>
        {showCpdPeriodSelect ? (
          <Row className='button-gutter'>
            <Col xs={12} md={4} mdOffset={8}>
              <form>
                <SelectFormField
                  emptyOption={false}
                  label='Choose report period'
                  value={yearsFromNow}
                  onChange={(e) => {
                    const newYearsFromNow = parseInt(e.target.value, 10);
                    const newSearchParam = queryString.stringify({
                      yearsFromNow: newYearsFromNow
                    });
                    goTo({ search: newSearchParam });
                  }}
                >
                  <option key={0} value={0}>
                    Current CPD year&nbsp; (
                    {formatDate(
                      addYears(new Date(fromDate), -yearsFromNow),
                      dateFormats.ShortDate
                    )}
                    &nbsp; -{' '}
                    {formatDate(
                      addYears(new Date(toDate), -yearsFromNow),
                      dateFormats.ShortDate
                    )}
                    )
                  </option>
                  <option key={1} value={-1}>
                    Last CPD year&nbsp; (
                    {formatDate(
                      addYears(new Date(fromDate), -(1 + yearsFromNow)),
                      dateFormats.ShortDate
                    )}
                    &nbsp; -{' '}
                    {formatDate(
                      addYears(new Date(toDate), -(1 + yearsFromNow)),
                      dateFormats.ShortDate
                    )}
                    )
                  </option>
                </SelectFormField>
              </form>
            </Col>
          </Row>
        ) : null}
        <Row className='no-gutter'>
          <Col xs={12} md={6} mdOffset={3}>
            <h1 className='flex-center'>{fullName(user)} Progress Report</h1>
          </Col>
          <Col xs={12} md={3}>
            <p>
              Activity Report: {formatDate(fromDate)} - {formatDate(toDate)}
            </p>
            <p>AFSL: {afsl}</p>
          </Col>
        </Row>
        <Row className='button-gutter bottom-gap'>
          <Col xs={12} md={2} mdOffset={8}>
            <Button className='large full pull-down-single' onClick={exportPdf}>
              <Icon
                iconSet='glyphicon'
                icon='download-alt'
                className='space-right'
              />{' '}
              Download as PDF
            </Button>
          </Col>
          <Col xs={12} md={2}>
            <Button className='large full pull-down-single' onClick={exportCsv}>
              <Icon
                iconSet='glyphicon'
                icon='download-alt'
                className='space-right'
              />{' '}
              Download as CSV
            </Button>
          </Col>
        </Row>
        <Row className='no-gutter'>
          <Col xs={12} className='flexbox'>
            {cpdProgress.goal > 0 ? ( // Only show the FASEA progress wheel if the FASEA goal > 0 hours
              <div className='right-gap'>
                <h3 className='text-center no-margin'>
                  {defaultProgressLabel}
                </h3>
                <div className='chart'>
                  <ProgressChart
                    progressPercentage={cpdProgress.progress}
                    width={100}
                    height={100}
                  />
                </div>
              </div>
            ) : null}
            {fp.map(({ goal, total, completed }) => {
              const progress = 100 * (completed / total);
              return (
                <div
                  key={goal.id}
                  className='flexbox vertical align-center right-gap'
                >
                  <h3 className='text-center no-margin'>{goal.name}</h3>
                  <div className='chart'>
                    <ProgressChart
                      progressPercentage={progress}
                      width={100}
                      height={100}
                    />
                  </div>
                </div>
              );
            })(goals)}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {trainingPlanSnapshot ? (
              <h3 className='flex-center'>
                {`This report is based on activity from ${formatDate(
                  fromDate
                )} to ${formatDate(
                  toDate
                )} against the training plan active at ${formatDate(
                  trainingPlanSnapshot.dateCreated
                )}.`}
              </h3>
            ) : null}
          </Col>
        </Row>
      </Grid>
    </section>
    <section
      id='cpd-progress-report'
      className='layout--section report-table-section'
    >
      <div className='container'>
        <SortableTable
          className='table--progress-report'
          rows={fp.filter(({ className }) => !fp.contains('hidden')(className))(
            progressTableRows
          )}
          columns={progressColumns}
        />
      </div>
    </section>
    <section
      id='cpd-activity-report'
      className='layout--section report-table-section'
    >
      <h1>{fullName(user)} Activity Details</h1>
      <div className='container'>
        <SortableTable
          className='table--activity-report'
          rows={activityTableRows}
          columns={activityColumns(goals)}
        />
      </div>
    </section>
  </div>
);

const mapStateToProps = (state) => ({
  cpdCategorys: state.cpdCategorys.cpdCategorys,
  publishers: state.publisher.publishers,
  userIndustry: fp.first(currentUserIndustry(state)),
  exportActivity: exportCpdSelector(state)
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(replace, dispatch),
  getCpdCategorys: bindActionCreators(
    cpdCategorysActions.cpdCategorys.get,
    dispatch
  ),
  getPublishers: bindActionCreators(publisherActions.publishers.get, dispatch),
  getExportCpdPoints: bindActionCreators(
    cpdPointActions.cpdPoints.getExport,
    dispatch
  )
});

/**
 * Produce the sub FASEA goal categories.
 * @return FASEA gaol categories rows.
 */
const faseaGoalCategoriesRows = (
  userCpdCategories,
  userIndustry,
  goal,
  cpdActivity,
  showDetails
) => {
  // We don't want the goal if the hours requires (goal) required is zero and the user has no activities.
  if (goal > 0 || cpdActivity.length > 0) {
    const getCpdCategoryRows = fp.flatMap(({ aoa, showChildren, children }) => {
      const singleCpdCategory = userCpdCategories.length <= 1;
      const categoryTypeClassName =
        aoa.categoryType === cpdCategoryType.sub ? ' childCpdCategory' : '';

      if (children) {
        return [
          {
            ...aoa,
            children,
            showChildren,
            className: (showDetails ? '' : 'hidden') + categoryTypeClassName
          },
          ...children.map((child) => ({
            ...child,
            className:
              child.className +
              (showChildren && (showDetails || singleCpdCategory)
                ? ''
                : ' hidden')
          }))
        ];
      }

      return [
        {
          ...aoa,
          children,
          showChildren,
          className:
            (showDetails || singleCpdCategory ? '' : 'hidden') +
            categoryTypeClassName
        }
      ];
    });

    const coreTopicCategories = userCpdCategories.filter(
      ({ aoa }) => aoa.categoryType === cpdCategoryType.core
    );
    const coreTopicCategoryRows = getCpdCategoryRows(coreTopicCategories);
    const coreTopicCategoryTitleRow =
      coreTopicCategoryRows.length > 0
        ? [
            {
              title: fp.getOr('Core Topics')('coreTopicLabel')(userIndustry),
              className:
                showDetails || userCpdCategories.length <= 1
                  ? 'heavy'
                  : 'hidden heavy',
              isTitleRow: true
            }
          ]
        : [];

    const subTopicCategories = userCpdCategories.filter(
      ({ aoa }) => aoa.categoryType === cpdCategoryType.sub
    );
    const subTopicCategoryRows = getCpdCategoryRows(subTopicCategories);
    const subTopicCategoryTitleRow =
      subTopicCategoryRows.length > 0
        ? [
            {
              title: fp.getOr('Sub Topics')('subTopicLabel')(userIndustry),
              className:
                showDetails || userCpdCategories.length <= 1
                  ? 'heavy'
                  : 'hidden heavy',
              isTitleRow: true
            }
          ]
        : [];

    return coreTopicCategoryTitleRow
      .concat(coreTopicCategoryRows)
      .concat(subTopicCategoryTitleRow)
      .concat(subTopicCategoryRows);
  }

  return [];
};

/**
 * Produce the non-FASEA goal categories, like TPD and RM.
 * @param faseaGoalCategories  FASEA goal categories rows.
 * @param {*} goals the user goals.
 * @param {*} cpdDaysRemaining user CPD remaining days.
 * @returns non FASEA gaol categories rows.
 */
const nonFaseaGoalCategoriesRows = (
  faseaGoalCategories,
  goals,
  cpdDaysRemaining
) => {
  const nonFaseaGoals = goals.map(({ goal, completed, total, shortFall }) => ({
    title: goal.name,
    trainingHoursMet: shortFall === 0 ? 'Yes' : 'No',
    goal: unfixedFloor(total, 2),
    progress: unfixedFloor(100 * (completed / total), 2),
    validCpd: unfixedFloor(completed, 2),
    shortFallPercentage: unfixedCeil(100 * (shortFall / total), 2),
    tasaCpd: 0,
    shortFall: unfixedCeil(shortFall, 2),
    riskRating: shortFall / Math.max(1, cpdDaysRemaining),
    className: 'normalCpdCategory heavy'
  }));

  return faseaGoalCategories.concat(nonFaseaGoals);
};

/**
 * Produce main FASEA goal categories rows.
 * @returns main FASEA goal categories rows.
 */
const mainFaseaGoalCategoriesRows = ({
  nonFaseaGoalCategoriesRows,
  userCpdCategories,
  goal,
  defaultProgressLabel,
  shortFall,
  progress,
  validCpd,
  shortFallPercentage,
  tasaCpd,
  riskRating,
  cpdActivity
}) => {
  let mainFaseaGoalRows = [];

  // We don't want the main gaol if the hours required (goal) is zero and the user has no activities.
  if (userCpdCategories.length > 1 && (goal > 0 || cpdActivity.length > 0)) {
    mainFaseaGoalRows = [
      {
        title: defaultProgressLabel,
        trainingHoursMet: shortFall === 0 ? 'Yes' : 'No',
        goal,
        progress,
        validCpd,
        shortFallPercentage,
        tasaCpd,
        shortFall,
        riskRating,
        className: 'normalCpdCategory heavy'
      }
    ];
  }

  return mainFaseaGoalRows.concat(nonFaseaGoalCategoriesRows);
};

/**
 * Produce the rows with CPD stats for area of advices progress.
 * @returns area of advice rows with CPD stats.
 */
const areaOfAdviceProgressRows = ({
  userCpdCategories,
  userIndustry,
  goal,
  showDetails,
  cpdDaysRemaining,
  goals,
  defaultProgressLabel,
  shortFall,
  progress,
  validCpd,
  shortFallPercentage,
  tasaCpd,
  riskRating,
  cpdActivity,
  toggleDetails,
  toggleChildren
}) => {
  const faseaGoalRows = faseaGoalCategoriesRows(
    userCpdCategories,
    userIndustry,
    goal,
    cpdActivity,
    showDetails
  );

  const nonFaseaGoalRows = nonFaseaGoalCategoriesRows(
    faseaGoalRows,
    goals,
    cpdDaysRemaining
  );

  const mainFaseaGoalRows = mainFaseaGoalCategoriesRows({
    nonFaseaGoalCategoriesRows: nonFaseaGoalRows,
    userCpdCategories,
    goal,
    defaultProgressLabel,
    shortFall,
    progress,
    validCpd,
    shortFallPercentage,
    tasaCpd,
    riskRating,
    cpdActivity
  });

  return mainFaseaGoalRows.map(
    (
      {
        cpdCategoryId,
        title,
        goal,
        progress,
        validCpd,
        shortFallPercentage,
        shortFall,
        riskRating,
        className,
        children,
        showChildren,
        isTitleRow
      },
      index
    ) => ({
      cpdCategory: title,
      cpdCategoryForDisplay:
        title === defaultProgressLabel ? (
          <div id='cpd-category-title' onClick={toggleDetails}>
            {title}
            {showDetails ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        ) : children && children.length > 0 ? (
          <div
            id='cpd-category-title'
            onClick={() => {
              toggleChildren(cpdCategoryId);
            }}
          >
            {title}
            {showChildren ? <FiChevronUp /> : <FiChevronDown />}
          </div>
        ) : (
          <div>{title}</div>
        ),
      trainingHoursMet: isTitleRow ? '' : shortFall === 0 ? 'Yes' : 'No',
      trainingPlanHrs: goal || '',
      totalProgress: goal > 0 ? `${progress}%` : '',
      totalProgressHrs: validCpd || '',
      shortfall: goal > 0 ? `${shortFallPercentage}%` : '',
      shortfallHrs: goal > 0 ? shortFall : '',
      // Add 1 to remaining days as we want the days left to include today.
      daysLeft: isTitleRow ? '' : Math.max(0, cpdDaysRemaining + 1),
      riskRating:
        goal > 0
          ? mapRiskRate(riskRating)
          : className === 'normalCpdCategory'
          ? '-'
          : '',
      className,
      children,
      showChildren
    })
  );
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const { cpdCategorys, getCpdCategorys, publishers, getPublishers } =
        this.props;

      if (!cpdCategorys.length) {
        getCpdCategorys();
      }

      if (!publishers.length) {
        getPublishers();
      }
    },
    componentWillReceiveProps(nextProps) {
      const {
        exportActivity,
        publishers,
        industryCpdCategorys,
        user,
        filter,
        userIndustryGoals
      } = this.props;
      const { exportActivity: cpdActivity } = nextProps;

      if (!exportActivity && cpdActivity) {
        const columns = getCpdActivityColumns(
          industryCpdCategorys,
          userIndustryGoals
        );
        const rows = mapCpdActivityForCsv(
          industryCpdCategorys,
          publishers,
          userIndustryGoals
        )(
          fp.flow(
            fp.uniqBy(({ userCpdPointId, cpdCategoryId }) => ({
              userCpdPointId,
              cpdCategoryId
            })),
            fp.sortBy(({ timeEarned }) => {
              return -1 * dateFnsCompat.parse(timeEarned).getTime();
            })
          )(cpdActivity)
        );
        const { firstName, lastName } = user;

        const endDate = dateFnsCompat
          .format(dateFnsCompat.parse(filter.toDate), dateFormats.ShortDate)
          .replace(/\//g, '-');

        downloadBlobStreamFile(
          writeDataToCsvStream(
            getCsvStream({
              header: true,
              columns
            }),
            rows
          ).pipe(getBlobStream()),
          `${firstName}-${lastName}-CPD-activity-${endDate}.csv`
        );
      }
    }
  }),
  branch(
    ({ cpdCategorys, publishers, cpdCategoryProgress }) =>
      !cpdCategorys || !publishers || !cpdCategoryProgress,
    renderComponent(Loading)
  ),
  withProps(({ cpdCategoryProgress, cpdCategorys, afsLicence }) => ({
    goals: fp.get('goals')(cpdCategoryProgress),
    cpdProgress: {
      ...cpdCategoryProgress.primaryStat,
      ...cpdCategoryProgress.derivedStat
    },
    afsl: afsLicence
      ? `${afsLicence.afsLicenceHolder} - (${afsLicence.afsLicenceNumber})`
      : null,
    userCpdCategorys: mapAllCategories(cpdCategoryProgress, cpdCategorys)
  })),
  withStateHandlers(
    ({ userCpdCategorys }) => ({
      showDetails: false,
      userCpdCategorysWithChildrenOption: fp.map((aoa) => ({
        ...aoa,
        showChildren: false
      }))(userCpdCategorys)
    }),
    {
      toggleDetails:
        ({ showDetails }) =>
        () => ({
          showDetails: !showDetails
        }),
      setUserCpdCategorysWithChildrenOption: () => (userCpdCategorys) => ({
        userCpdCategorysWithChildrenOption: fp.map((aoa) => ({
          ...aoa,
          showChildren: false
        }))(userCpdCategorys)
      }),
      toggleChildren:
        ({ userCpdCategorysWithChildrenOption }) =>
        (aoaIdToToggle) => ({
          userCpdCategorysWithChildrenOption:
            userCpdCategorysWithChildrenOption.map((cpdCategoryWithChildren) =>
              cpdCategoryWithChildren.aoa.cpdCategoryId === aoaIdToToggle
                ? {
                    ...cpdCategoryWithChildren,
                    showChildren: !cpdCategoryWithChildren.showChildren
                  }
                : cpdCategoryWithChildren
            )
        })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const { userCpdCategorys, setUserCpdCategorysWithChildrenOption } =
        this.props;

      // When user cpd category progress changed, update the corresponding cpd category progress state (with show children option).
      if (!fp.isEqual(prevProps.userCpdCategorys, userCpdCategorys)) {
        setUserCpdCategorysWithChildrenOption(userCpdCategorys);
      }
    }
  }),
  withProps(
    ({
      cpdCategorys,
      cpdActivity,
      cpdDaysRemaining,
      cpdProgress: {
        goal,
        validCpd,
        shortFallPercentage,
        progress,
        tasaCpd,
        shortFall,
        riskRating
      },
      publishers,
      userIndustry,
      userCpdCategorysWithChildrenOption,
      toggleChildren,
      goals,
      defaultProgressLabel,
      showDetails,
      toggleDetails
    }) => ({
      progressTableRows: areaOfAdviceProgressRows({
        userCpdCategories: userCpdCategorysWithChildrenOption,
        userIndustry,
        goal,
        showDetails,
        goals,
        cpdDaysRemaining,
        defaultProgressLabel,
        shortFall,
        progress,
        validCpd,
        shortFallPercentage,
        tasaCpd,
        riskRating,
        cpdActivity,
        toggleDetails,
        toggleChildren
      }),
      activityTableRows: mapCpdActivity(
        cpdCategorys,
        publishers,
        goals
      )(
        fp.flow(
          fp.uniqBy(({ userCpdPointId, cpdCategoryId }) => ({
            userCpdPointId,
            cpdCategoryId
          })),
          fp.sortBy(({ timeEarned }) => {
            return -1 * new Date(timeEarned).getTime();
          })
        )(cpdActivity)
      )
    })
  ),
  withHandlers({
    exportCsv:
      ({ filter, getExportCpdPoints, reporteeId }) =>
      (e) => {
        getExportCpdPoints(
          {
            ...filter,
            take: undefined
          },
          reporteeId
        );
      },
    exportPdf:
      ({
        afsl,
        activityTableRows,
        progressTableRows,
        cpdProgress: { progress, goal },
        filter,
        user,
        goals,
        defaultProgressLabel
      }) =>
      (e) => {
        exportCpdReportPdf({
          afsl,
          cpdActivity: {
            columns: fp.map((col) => col.display())(activityColumns(goals)),
            rows: fp.map((row) =>
              fp.map((col) =>
                fp.get(col.name)({
                  ...row,
                  cpdCategory: row.hasMultipleCpdCategory
                    ? 'Multiple'
                    : row.cpdCategory
                })
              )(activityColumns(goals))
            )(activityTableRows)
          },
          filter,
          progress,
          progressTableRows,
          user,
          goals,
          defaultProgressLabel,
          goal
        });
      }
  })
)(ProgressReport);



// WEBPACK FOOTER //
// ./src/containers/pages/shared/progress-report.js